import {useEffect} from 'react';

import {selectActiveThread} from './store/selectors';
import type {AgentChatStore} from './store/store';
import {markThreadRead} from './threadReadStatus';

/**
 * Marks the active thread as read on the two events that imply the user has
 * seen its current state: switching into a settled thread, or watching an
 * in-view thread settle. Running threads aren't marked on switch — peeking
 * at an in-flight run shouldn't pre-empt its completion dot.
 *
 * Stamps with `cur.lastActivityAt` (a server timestamp) rather than
 * `Date.now()`. The `turn-created` event keeps `lastActivityAt` on the server
 * clock immediately, so the read marker and the unread predicate compare
 * server-to-server — no client/server clock skew, and a later history refresh
 * can't advance `lastActivityAt` past the stamp and resurrect the dot.
 */
export function useThreadReadTracker(store: AgentChatStore): void {
  useEffect(() => {
    let prev = selectActiveThread(store.getState());

    return store.subscribe(() => {
      const cur = selectActiveThread(store.getState());
      const switched = cur?.id !== prev?.id;
      const settled = cur != null && cur.status !== 'running';

      const openedSettled = switched && settled;
      const finishedInView = !switched && prev?.status === 'running' && settled;

      if ((openedSettled || finishedInView) && cur?.id != null) {
        markThreadRead(cur.id, cur.lastActivityAt);
      }

      prev = cur;
    });
  }, [store]);
}
